import { FieldMessage } from 'apiConnector/secondary-market/batch/base';
import type { OrderType } from 'apiConnector/secondary-market/batch/model';
import { IntlKey } from 'locales';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { ApiMessage } from 'utils/http/httpClient.model';

function isFieldMessage(validation: ApiMessage & Partial<FieldMessage>): validation is FieldMessage {
  return validation.code !== undefined && validation.value !== undefined;
}
type RemainingTradeway = FieldMessage<'remaining-way', { tradeWay: TradeWay; remaining: number }>;
function isRemainingTradeway(validation: ApiMessage & Partial<FieldMessage>): validation is RemainingTradeway {
  return validation.code === 'remaining-way' && typeof validation.value === 'object';
}

const useHandleMessageValueByCode = () => {
  const { formatNumber, formatMessage } = useIntl();
  return useCallback(
    (fieldMessage: FieldMessage) => {
      if (isRemainingTradeway(fieldMessage)) {
        return {
          tradeWay: formatMessage({
            id: `secondaryMarket.productSummary.label.${fieldMessage.value.tradeWay === 'ClientBuy' ? 'sgSell' : 'sgBuy'}`,
          }),
          remaining: formatNumber(fieldMessage.value.remaining),
        };
      }
      if (fieldMessage.code === 'remaining') {
        return formatNumber(fieldMessage.value as number);
      }
      return fieldMessage.value;
    },
    [formatNumber, formatMessage],
  );
};
export function useMapMessage(orderType: OrderType) {
  const { formatMessage } = useIntl();
  const handleMessageValueByCode = useHandleMessageValueByCode();
  return useCallback(
    (validation: ApiMessage & Partial<FieldMessage>) => {
      const valueByCode = isFieldMessage(validation) ? handleMessageValueByCode(validation) : validation.value;
      const value = typeof valueByCode === 'object' ? valueByCode : { value: valueByCode };
      return (
        validation.message ??
        formatMessage(
          { id: `secondaryMarket.batch.field.${validation.code}` as IntlKey },
          {
            field: formatMessage({
              id: `secondary-market-batch-grid.${orderType}.column.header.${validation.fieldName}` as IntlKey,
            }),
            ...value,
          },
        )
      );
    },
    [formatMessage, orderType, handleMessageValueByCode],
  );
}
